import { Trans, useLingui } from "@lingui/react/macro";
import type { Bot, CompanyHiringOverview, CompanyHiringRequest, Group } from "@rakazo/contracts";
import { GROUP_NAME_MAX_LENGTH, isCompanyManagerSpawnKey } from "@rakazo/contracts";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  BotAvatar,
  Button,
  Input,
} from "@rakazo/ui-web";
import { Check, UserMinus, X } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useId, useRef, useState } from "react";
import type { RpcClient } from "../lib/rpc";
import { isTeamLeader } from "../lib/teams";
import { errorText } from "../lib/user-error";
import { DeleteItemDialog } from "./shell/dialogs";

export function GroupSettings({
  group,
  bots,
  company = false,
  onSave,
  onRemove,
  onClose,
  onOpenBot,
  covered = false,
  showHeader = true,
  topContent,
  rpc,
  onCompanyChanged,
}: {
  group: Group;
  bots: Bot[];
  company?: boolean;
  onSave: (input: { name?: string; botIds?: string[] }) => Promise<void>;
  rpc?: RpcClient;
  onCompanyChanged?: () => Promise<void>;
  onRemove: () => Promise<void>;
  onClose: () => void;
  onOpenBot?: (botId: string) => void;
  /** A full-screen overlay is on top, so Escape belongs to it. */
  covered?: boolean;
  /** The permanent right panel draws the title and never closes. */
  showHeader?: boolean;
  /** Company-wide content above the name and members, such as the shared computer. */
  topContent?: ReactNode;
}) {
  const { t } = useLingui();
  const nameId = useId();
  const [name, setName] = useState(group.name);
  // The panel can open on a cached group. A refreshed one replaces the name unless it was edited.
  const [shownGroup, setShownGroup] = useState(group);
  if (group !== shownGroup) {
    setShownGroup(group);
    if (name === shownGroup.name) setName(group.name);
  }
  const [saving, setSaving] = useState(false);
  const [companyAction, setCompanyAction] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [dismissTarget, setDismissTarget] = useState<Group["members"][number] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [companyTab, setCompanyTab] = useState<"store" | "employees" | "market">("store");
  const [hiring, setHiring] = useState<CompanyHiringOverview | null>(null);
  const [hiringLoading, setHiringLoading] = useState(false);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!company || !rpc) {
      setHiring(null);
      setHiringLoading(false);
      return;
    }
    let current = true;
    setHiringLoading(true);
    void rpc.companies.hiring
      .list({ spaceId: group.spaceId })
      .then((overview) => {
        if (current) setHiring(overview);
      })
      .catch((cause) => {
        if (current) setError(errorText(cause, t`Could not load company employees`));
      })
      .finally(() => {
        if (current) setHiringLoading(false);
      });
    return () => {
      current = false;
    };
  }, [company, group.spaceId, rpc, t]);

  // Closing while a save is in flight would let its completion close the panel
  // again after a reopen. While the delete confirmation or an overlay is open,
  // Escape belongs to it.
  useEffect(() => {
    if (!showHeader || saving || companyAction || confirmingDelete || dismissTarget || covered)
      return;
    const onKey = (event: KeyboardEvent) => {
      if (
        event.key === "Escape" &&
        !event.defaultPrevented &&
        !event.isComposing &&
        event.keyCode !== 229
      ) {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [saving, companyAction, confirmingDelete, dismissTarget, covered, onClose, showHeader]);

  async function save() {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      // Membership is managed separately; this panel only renames the chat.
      await onSave({ name: name.trim() !== group.name ? name.trim() : undefined });
    } catch (cause) {
      setError(errorText(cause, t`Could not save group`));
      return;
    } finally {
      setSaving(false);
    }
    // A save that finishes after the user switched chats must not close
    // whatever panel is open there.
    if (mounted.current) onClose();
  }

  async function refreshCompanyAfterChange() {
    if (!rpc) return;
    try {
      const overview = await rpc.companies.hiring.list({ spaceId: group.spaceId });
      if (mounted.current) setHiring(overview);
    } catch (cause) {
      if (mounted.current) setError(errorText(cause, t`Could not refresh company employees`));
    }
    if (onCompanyChanged) await onCompanyChanged().catch(() => undefined);
  }

  async function decideCompanyHire(request: CompanyHiringRequest, decision: "approve" | "decline") {
    if (!rpc || !hiring?.canApprove || companyAction || request.status !== "pending") return;
    setCompanyAction(true);
    setError(null);
    try {
      const input = { spaceId: group.spaceId, requestId: request.id, revision: request.revision };
      if (decision === "approve") await rpc.companies.hiring.approve(input);
      else await rpc.companies.hiring.decline(input);
      await refreshCompanyAfterChange();
    } catch (cause) {
      setError(errorText(cause, t`Could not update hiring proposal`));
    } finally {
      setCompanyAction(false);
    }
  }

  async function dismissEmployee() {
    if (!rpc || !hiring?.canApprove || !dismissTarget || companyAction) return;
    const target = dismissTarget;
    setCompanyAction(true);
    setError(null);
    try {
      await rpc.companies.hiring.archiveEmployee({ spaceId: group.spaceId, botId: target.botId });
      if (mounted.current) setDismissTarget(null);
      await refreshCompanyAfterChange();
    } catch (cause) {
      setError(errorText(cause, t`Could not dismiss employee`));
    } finally {
      setCompanyAction(false);
    }
  }

  const nameField = (
    <label htmlFor={nameId} className="block text-sm text-muted-foreground">
      <span className="shrink-0">
        <Trans>Group name</Trans>
      </span>
      <Input
        id={nameId}
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={GROUP_NAME_MAX_LENGTH}
        className="mt-2"
      />
    </label>
  );
  const memberRow = (member: Group["members"][number], companyStyle: boolean) => {
    const memberBot = bots.find((bot) => bot.id === member.botId);
    return (
      <button
        key={member.botId}
        type="button"
        onClick={() => onOpenBot?.(member.botId)}
        className={
          companyStyle
            ? "flex min-w-0 items-center gap-2 rounded-lg bg-surface px-2 py-2 text-start transition-colors hover:bg-surface-hover"
            : "flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 text-start hover:bg-accent"
        }
      >
        <BotAvatar
          avatarSet={member.avatarSet}
          color={member.color}
          identity={member.botId}
          size={32}
          status={member.status}
        />
        <span className="flex min-w-0 flex-1 items-center gap-1.5" dir="auto">
          <span className="truncate text-[15px] text-foreground">{member.name}</span>
          {memberBot && isTeamLeader(memberBot.spawnKey) ? (
            <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
              <Trans>Lead</Trans>
            </span>
          ) : null}
        </span>
      </button>
    );
  };
  const employeesGrid = (
    <div className="min-h-0 flex-1 overflow-y-auto">
      {hiring || hiringLoading ? (
        <div className="mb-3 flex items-center justify-between text-sm text-muted-foreground">
          <span>
            <Trans>Active employees</Trans>
          </span>
          <span>
            {hiring ? `${hiring.memberCount}/${hiring.memberLimit}` : <Trans>Loading…</Trans>}
          </span>
        </div>
      ) : null}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-2">
        {group.members.map((member) => {
          const memberBot = bots.find((bot) => bot.id === member.botId);
          const canDismiss = Boolean(
            hiring?.canApprove &&
              memberBot &&
              !memberBot.archivedAt &&
              !isTeamLeader(memberBot.spawnKey),
          );
          return (
            <div key={member.botId} className="relative min-w-0">
              <button
                type="button"
                onClick={() => onOpenBot?.(member.botId)}
                className={`flex w-full min-w-0 items-center gap-2 rounded-lg bg-surface px-2 py-2 text-start transition-colors hover:bg-surface-hover ${
                  canDismiss ? "pe-9" : ""
                }`}
              >
                <BotAvatar
                  avatarSet={member.avatarSet}
                  color={member.color}
                  identity={member.botId}
                  size={32}
                  status={member.status}
                />
                <span className="flex min-w-0 flex-1 items-center gap-1.5" dir="auto">
                  <span className="truncate text-[15px] text-foreground">{member.name}</span>
                  {memberBot && isTeamLeader(memberBot.spawnKey) ? (
                    <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                      <Trans>Lead</Trans>
                    </span>
                  ) : null}
                  {memberBot && isCompanyManagerSpawnKey(memberBot.spawnKey) ? (
                    <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                      <Trans>Supervisor</Trans>
                    </span>
                  ) : null}
                </span>
              </button>
              {canDismiss ? (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t`Dismiss ${member.name}`}
                  disabled={companyAction}
                  onClick={() => setDismissTarget(member)}
                  className="absolute end-1 top-1/2 -translate-y-1/2 text-foreground-subtle hover:text-destructive"
                >
                  <UserMinus size={15} />
                </Button>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
  const proposalsSection = hiring ? (
    <section className="space-y-3" aria-label={t`Hiring proposals`}>
      <h3 className="text-sm font-medium text-foreground">
        <Trans>Hiring proposals</Trans>
      </h3>
      {hiring.requests.length ? (
        hiring.requests.map((request) => (
          <article key={request.id} className="rounded-md border border-border p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h4 className="truncate text-sm font-medium text-foreground">
                  {request.snapshot.title}
                </h4>
                <p className="text-xs text-muted-foreground">
                  <Trans>Revision {request.revision}</Trans>
                </p>
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">
                {request.status === "pending" ? (
                  <Trans>Pending</Trans>
                ) : request.status === "hired" ? (
                  <Trans>Hired</Trans>
                ) : (
                  <Trans>Declined</Trans>
                )}
              </span>
            </div>
            <div className="mt-3 space-y-2 text-sm">
              <p>
                <strong>
                  <Trans>Role</Trans>:
                </strong>{" "}
                {request.snapshot.summary}
              </p>
              <p>
                <strong>
                  <Trans>Reason</Trans>:
                </strong>{" "}
                {request.snapshot.rationale}
              </p>
              <p>
                <strong>
                  <Trans>Evidence</Trans>:
                </strong>{" "}
                {request.snapshot.evidence}
              </p>
              <p>
                <strong>
                  <Trans>Coverage gap</Trans>:
                </strong>{" "}
                {request.snapshot.coverageGap}
              </p>
              <p>
                <strong>
                  <Trans>First task</Trans>:
                </strong>{" "}
                {request.snapshot.firstTask}
              </p>
              <p>
                <strong>
                  <Trans>Acceptance criteria</Trans>:
                </strong>{" "}
                {request.snapshot.firstTaskAcceptanceCriteria}
              </p>
            </div>
            <details className="mt-3 text-sm">
              <summary className="cursor-pointer text-muted-foreground">
                <Trans>Role details</Trans>
              </summary>
              <div className="mt-2 space-y-2">
                <p>
                  <strong>
                    <Trans>Responsibilities</Trans>:
                  </strong>{" "}
                  {request.snapshot.responsibilities.join("; ")}
                </p>
                <p>
                  <strong>
                    <Trans>Required skills</Trans>:
                  </strong>{" "}
                  {request.snapshot.requiredSkills.join("; ")}
                </p>
                <p>
                  <strong>
                    <Trans>Deliverables</Trans>:
                  </strong>{" "}
                  {request.snapshot.deliverables.join("; ")}
                </p>
                <p>
                  <strong>
                    <Trans>Role acceptance criteria</Trans>:
                  </strong>{" "}
                  {request.snapshot.acceptanceCriteria.join("; ")}
                </p>
                <p>
                  <strong>
                    <Trans>Escalation boundary</Trans>:
                  </strong>{" "}
                  {request.snapshot.escalation}
                </p>
                <p>
                  <strong>
                    <Trans>Instructions</Trans>:
                  </strong>{" "}
                  {request.snapshot.instructions}
                </p>
                {request.snapshot.additionalInstructions ? (
                  <p>
                    <strong>
                      <Trans>Additional instructions</Trans>:
                    </strong>{" "}
                    {request.snapshot.additionalInstructions}
                  </p>
                ) : null}
              </div>
            </details>
            {hiring.canApprove && request.status === "pending" ? (
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  disabled={companyAction || hiring.memberCount >= hiring.memberLimit}
                  onClick={() => void decideCompanyHire(request, "approve")}
                >
                  <Check size={15} />
                  <Trans>Approve</Trans>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={companyAction}
                  onClick={() => void decideCompanyHire(request, "decline")}
                >
                  <X size={15} />
                  <Trans>Decline</Trans>
                </Button>
              </div>
            ) : null}
          </article>
        ))
      ) : (
        <p className="text-sm text-muted-foreground">
          <Trans>No hiring proposals</Trans>
        </p>
      )}
    </section>
  ) : null;
  const marketPanel = (
    <div className="min-h-0 flex-1 space-y-5 overflow-y-auto">
      {hiring?.positions.length ? (
        <div className="space-y-2">
          {hiring.positions.map((position) => (
            <article key={position.id} className="rounded-md border border-border p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h4 className="truncate text-sm font-medium text-foreground">{position.title}</h4>
                  <p className="text-xs text-muted-foreground">{position.summary}</p>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {position.status === "available" ? (
                    <Trans>Available</Trans>
                  ) : position.status === "pending" ? (
                    <Trans>Pending</Trans>
                  ) : (
                    <Trans>Filled</Trans>
                  )}
                </span>
              </div>
            </article>
          ))}
        </div>
      ) : null}
      {proposalsSection}
    </div>
  );
  const membersSection = (
    <div className="mt-5 min-h-0 flex-1">
      <span className="text-sm text-muted-foreground">
        <Trans>Members ({group.members.length})</Trans>
      </span>
      <div className="mt-2 space-y-1">
        {group.members.map((member) => memberRow(member, false))}
      </div>
    </div>
  );
  const actionButtons = (
    <div className="mt-5 flex gap-2">
      <Button className="flex-1" disabled={saving || !name.trim()} onClick={() => void save()}>
        {saving ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
      </Button>
      <Button
        variant="destructive"
        className="flex-1"
        disabled={saving}
        onClick={() => setConfirmingDelete(true)}
      >
        <Trans>Delete group</Trans>
      </Button>
    </div>
  );
  const groupInfo = (
    <>
      {nameField}
      {membersSection}
      {actionButtons}
    </>
  );
  const companyTabs = (
    <div role="tablist" aria-label={t`Company overview`} className="mb-4 flex items-center gap-1">
      <button
        type="button"
        role="tab"
        aria-selected={companyTab === "store"}
        onClick={() => setCompanyTab("store")}
        className={`rounded-md px-2.5 py-1 text-ui-base transition-colors ${
          companyTab === "store"
            ? "bg-selected font-medium text-foreground"
            : "text-foreground-subtle hover:bg-hover hover:text-foreground"
        }`}
      >
        <Trans>Store</Trans>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={companyTab === "employees"}
        onClick={() => setCompanyTab("employees")}
        className={`rounded-md px-2.5 py-1 text-ui-base transition-colors ${
          companyTab === "employees"
            ? "bg-selected font-medium text-foreground"
            : "text-foreground-subtle hover:bg-hover hover:text-foreground"
        }`}
      >
        <Trans>Employees</Trans>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={companyTab === "market"}
        onClick={() => setCompanyTab("market")}
        className={`rounded-md px-2.5 py-1 text-ui-base transition-colors ${
          companyTab === "market"
            ? "bg-selected font-medium text-foreground"
            : "text-foreground-subtle hover:bg-hover hover:text-foreground"
        }`}
      >
        <Trans>Talent market</Trans>
      </button>
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      {showHeader ? (
        <div className="mb-4 flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {company ? <Trans>Company overview</Trans> : <Trans>Group settings</Trans>}
          </span>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t`Close panel`}
            disabled={saving}
            onClick={onClose}
          >
            <X size={16} strokeWidth={1.8} />
          </Button>
        </div>
      ) : null}
      {topContent}
      {error ? (
        <p role="alert" className="mb-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}
      {company ? (
        <>
          {companyTabs}
          {companyTab === "employees" ? employeesGrid : null}
          {companyTab === "market" ? marketPanel : null}
        </>
      ) : (
        groupInfo
      )}
      {confirmingDelete ? (
        <DeleteItemDialog
          item={group}
          noun="group"
          description={<Trans>Bots and their own chats stay.</Trans>}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={onRemove}
        />
      ) : null}
      {dismissTarget ? (
        <AlertDialog
          open
          onOpenChange={(open) => {
            if (!open && !companyAction) setDismissTarget(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                <Trans>Dismiss {dismissTarget.name}?</Trans>
              </AlertDialogTitle>
              <AlertDialogDescription>
                <Trans>
                  Their chat history and files are kept. Open tickets return to the unassigned
                  queue.
                </Trans>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={companyAction}>
                <Trans>Cancel</Trans>
              </AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                disabled={companyAction}
                onClick={() => void dismissEmployee()}
              >
                <UserMinus size={15} />
                {companyAction ? <Trans>Dismissing…</Trans> : <Trans>Dismiss employee</Trans>}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </div>
  );
}
